/**
 * useMentionInput — verbindet ein Chat-Eingabefeld mit der @-Auswahlliste.
 *
 * Der Hook hält den Suchbegriff, die Kandidaten und die Tastaturnavigation;
 * die Erkennung selbst steckt in lib/mentions.ts, die Darstellung in
 * MentionPopover. Beide Chats (Team-Chat, Kunden-Chat) benutzen ihn.
 *
 * Verwendung:
 *   const m = useMentionInput({ value, onChange: setValue, inputRef });
 *   <textarea ... onKeyDown={e => { if (m.handleKeyDown(e)) return; ...eigene Logik }} />
 *   <MentionPopover ... {...m.popoverProps} />
 *
 * `handleKeyDown` gibt true zurück, wenn die Taste zur Auswahlliste gehörte —
 * der Aufrufer darf sie dann nicht mehr als „Senden" behandeln.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isActiveAdmin, v1Admins, type V1Admin } from '../../lib/v1';
import { applyMention, filterAdminsForMention, findMentionQuery } from '../../lib/mentions';

interface UseMentionInputOptions {
  value: string;
  onChange: (next: string) => void;
  inputRef: React.RefObject<HTMLTextAreaElement | HTMLInputElement | null>;
}

export function useMentionInput({ value, onChange, inputRef }: UseMentionInputOptions) {
  const [admins, setAdmins] = useState<V1Admin[]>([]);
  const [caret, setCaret] = useState(0);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  // Adminliste einmal laden. Schlägt sie fehl, bleibt die Eingabe normal
  // benutzbar — nur ohne Vorschläge.
  useEffect(() => {
    let alive = true;
    void v1Admins.list()
      // Vorschläge nur für aktive Mitarbeiter (Damon 11.10.2026).
      .then(list => { if (alive) setAdmins(list.filter(isActiveAdmin)); })
      .catch(() => { if (alive) setAdmins([]); });
    return () => { alive = false; };
  }, []);

  const mention = useMemo(() => (open ? findMentionQuery(value, caret) : null), [open, value, caret]);
  const candidates = useMemo(
    () => (mention ? filterAdminsForMention(admins, mention.query) : []),
    [mention, admins],
  );

  // Der markierte Eintrag darf nicht aus der geschrumpften Liste laufen.
  useEffect(() => { setActiveIndex(0); }, [mention?.query]);

  const isOpen = mention !== null && candidates.length > 0;

  /** Nach jeder Eingabe: Text und Cursorposition übernehmen. */
  const handleInput = useCallback((e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    onChange(e.target.value);
    setCaret(e.target.selectionStart ?? e.target.value.length);
    setOpen(true);
  }, [onChange]);

  /** Cursorbewegung per Klick/Pfeiltaste ändert den erkannten Suchbegriff. */
  const handleSelect = useCallback((e: React.SyntheticEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setCaret(e.currentTarget.selectionStart ?? 0);
  }, []);

  const pick = useCallback((admin: V1Admin) => {
    const current = findMentionQuery(value, caret);
    if (!current) return;

    const next = applyMention(value, current, admin, caret);
    onChange(next.text);
    setCaret(next.caret);
    setOpen(false);

    // Cursor hinter die eingefügte Erwähnung setzen, sobald React den neuen
    // Wert gerendert hat.
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(next.caret, next.caret);
    });
  }, [value, caret, onChange, inputRef]);

  /** @return true, wenn die Taste von der Auswahlliste verbraucht wurde. */
  const handleKeyDown = useCallback((e: React.KeyboardEvent): boolean => {
    if (!isOpen) return false;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => (i + 1) % candidates.length);
      return true;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => (i - 1 + candidates.length) % candidates.length);
      return true;
    }
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      pick(candidates[activeIndex]);
      return true;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      return true;
    }
    return false;
  }, [isOpen, candidates, activeIndex, pick]);

  return {
    /** An das Eingabefeld: onChange/onSelect/onKeyDown-Vorschaltung. */
    handleInput,
    handleSelect,
    handleKeyDown,
    /** An MentionPopover. */
    popoverProps: { candidates: isOpen ? candidates : [], activeIndex, onPick: pick },
    /** Nach dem Absenden aufrufen, damit die Liste nicht offen bleibt. */
    reset: useCallback(() => { setOpen(false); setCaret(0); }, []),
  };
}
